import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Flex, Stack, Surface } from "@/components/layout";
import { Text } from "@/components/typography";
import {
  ActionBar,
  Button,
  Card,
  EmptyState,
  LoadingState,
  SectionHeader,
  StatusChip,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useToast,
} from "@/components/ui";
import { apiErrorMessage } from "@/api/client";
import { useApiClient } from "@/api/useApiClient";
import { usePageBreadcrumbs } from "@/shell/usePageBreadcrumbs";
import { createResourcesApi } from "../api";
import { formatLabel, resourceStatusTone, scheduleStatusTone } from "../labels";
import type {
  AvailabilityDto,
  BlackoutDto,
  MaintenanceDto,
  PolicyDto,
  ResourceDetailResponse,
  ScheduleWindowAction,
} from "../types";
import {
  AttachFileDialog,
  EditResourceDialog,
  ReasonActionDialog,
  ResourceActionDialog,
} from "../components/ResourceDialogs";
import {
  CreateAvailabilityDialog,
  CreateBlackoutDialog,
  CreateImpactDialog,
  CreateMaintenanceDialog,
  CreatePolicyDialog,
  MaintenanceActionDialog,
  PolicyActionDialog,
} from "../components/SupplyDialogs";

export function ResourceDetailPage() {
  const { resourceId = "" } = useParams();
  const navigate = useNavigate();
  const client = useApiClient();
  const api = useMemo(() => createResourcesApi(client), [client]);
  const toast = useToast();

  const [detail, setDetail] = useState<ResourceDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const [statusOpen, setStatusOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [attachOpen, setAttachOpen] = useState(false);
  const [availabilityOpen, setAvailabilityOpen] = useState(false);
  const [blackoutOpen, setBlackoutOpen] = useState(false);
  const [maintenanceOpen, setMaintenanceOpen] = useState(false);
  const [policyOpen, setPolicyOpen] = useState(false);
  const [scheduleAction, setScheduleAction] = useState<{
    kind: "availability" | "blackout";
    id: string;
    action: ScheduleWindowAction;
  } | null>(null);
  const [maintTarget, setMaintTarget] = useState<string | null>(null);
  const [impactTarget, setImpactTarget] = useState<string | null>(null);
  const [policyTarget, setPolicyTarget] = useState<string | null>(null);

  usePageBreadcrumbs([
    { label: "HUDDL HQ", to: "/app" },
    { label: "Business Center", to: "/app/businesses" },
    { label: "Bookable Resources", to: "/app/businesses/resources" },
    { label: detail?.resource.name ?? "Resource Detail" },
  ]);

  const load = useCallback(async () => {
    if (!resourceId) return;
    setLoading(true);
    setError(null);
    try {
      setDetail(await api.getDetail(resourceId));
    } catch (err) {
      setError(apiErrorMessage(err));
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [api, resourceId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <LoadingState message="Loading Bookable Resource…" />;
  }

  if (error || !detail) {
    return (
      <EmptyState
        title="Resource unavailable"
        description={error ?? "Resource not found or not authorized."}
        reason="scope"
        action={
          <Button variant="secondary" onClick={() => void load()}>
            Retry
          </Button>
        }
      />
    );
  }

  const { resource, availability, blackouts, maintenance, policies } = detail;

  return (
    <Stack gap={24}>
      <SectionHeader
        title={resource.name}
        description={`${formatLabel(resource.resource_type)} · ${resource.id}`}
        actions={
          <Flex gap={8} wrap>
            <Button
              variant="secondary"
              onClick={() =>
                void navigate(
                  `/app/bookings?bookable_resource_id=${resource.id}`,
                )
              }
            >
              Bookings
            </Button>
            <Button
              variant="secondary"
              onClick={() =>
                void navigate(`/app/businesses/${resource.business_id}`)
              }
            >
              Open Business
            </Button>
            <Button variant="secondary" onClick={() => setEditOpen(true)}>
              Edit
            </Button>
            <Button variant="secondary" onClick={() => setStatusOpen(true)}>
              Status actions
            </Button>
            <Button variant="secondary" onClick={() => setAttachOpen(true)}>
              Attach file
            </Button>
          </Flex>
        }
      />

      <ActionBar
        left={
          <Flex gap={8} align="center" wrap>
            <StatusChip tone={resourceStatusTone(resource.status)}>
              Status: {formatLabel(resource.status)}
            </StatusChip>
            <StatusChip tone="info">
              Visibility: {formatLabel(resource.visibility_status)}
            </StatusChip>
          </Flex>
        }
      />

      <Card
        header={
          <Text role="title" as="h3">
            Identity
          </Text>
        }
      >
        <Stack gap={8}>
          <Text role="body" as="p">
            Business ID: {resource.business_id}
          </Text>
          <Text role="body" as="p">
            Capability ID: {resource.business_capability_id}
          </Text>
          <Text role="body" as="p">
            Market ID: {resource.market_id}
          </Text>
          <Text role="body" as="p">
            Capacity: {resource.capacity ?? "—"}
          </Text>
        </Stack>
      </Card>

      <SupplySection
        title="Availability windows"
        emptyTitle="No availability windows"
        emptyDescription="Add when this resource can be booked. Bookings themselves are a later module."
        onAdd={() => setAvailabilityOpen(true)}
        addLabel="Add window"
      >
        {availability.length > 0 ? (
          <AvailabilityTable
            rows={availability}
            onAction={(id, action) =>
              setScheduleAction({ kind: "availability", id, action })
            }
          />
        ) : undefined}
      </SupplySection>

      <SupplySection
        title="Blackouts"
        emptyTitle="No blackouts"
        emptyDescription="Blackouts block booking during closures, private use, or safety holds."
        onAdd={() => setBlackoutOpen(true)}
        addLabel="Add blackout"
      >
        {blackouts.length > 0 ? (
          <BlackoutTable
            rows={blackouts}
            onAction={(id, action) =>
              setScheduleAction({ kind: "blackout", id, action })
            }
          />
        ) : undefined}
      </SupplySection>

      <SupplySection
        title="Maintenance"
        emptyTitle="No maintenance events"
        emptyDescription="Maintenance can take a resource offline and record Booking impacts without operating Bookings here."
        onAdd={() => setMaintenanceOpen(true)}
        addLabel="Add maintenance"
      >
        {maintenance.length > 0 ? (
          <MaintenanceTable
            rows={maintenance}
            onAction={(id) => setMaintTarget(id)}
            onImpact={(id) => setImpactTarget(id)}
          />
        ) : undefined}
      </SupplySection>

      <SupplySection
        title="Pricing and visibility policies"
        emptyTitle="No policies"
        emptyDescription="Policies control price, promotion, visibility, and discovery. High-impact changes may need Approval."
        onAdd={() => setPolicyOpen(true)}
        addLabel="Add policy"
      >
        {policies.length > 0 ? (
          <PolicyTable
            rows={policies}
            onAction={(id) => setPolicyTarget(id)}
          />
        ) : undefined}
      </SupplySection>

      <ResourceActionDialog
        open={statusOpen}
        onClose={() => {
          setStatusOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.applyAction(resource.id, input.action, {
              reason: input.reason,
            });
            toast.push({
              title: "Resource action applied",
              description: formatLabel(input.action),
              tone: "success",
            });
            setStatusOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <EditResourceDialog
        open={editOpen}
        name={resource.name}
        capacity={resource.capacity}
        visibilityStatus={resource.visibility_status}
        onClose={() => {
          setEditOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (patch) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.update(resource.id, patch);
            toast.push({ title: "Resource updated", tone: "success" });
            setEditOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <AttachFileDialog
        open={attachOpen}
        onClose={() => {
          setAttachOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.attachFile(resource.id, input);
            toast.push({ title: "File Asset attached", tone: "success" });
            setAttachOpen(false);
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <CreateAvailabilityDialog
        open={availabilityOpen}
        onClose={() => {
          setAvailabilityOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.createAvailability({
              business_id: resource.business_id,
              bookable_resource_id: resource.id,
              ...input,
            });
            toast.push({ title: "Availability created", tone: "success" });
            setAvailabilityOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <CreateBlackoutDialog
        open={blackoutOpen}
        onClose={() => {
          setBlackoutOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.createBlackout({
              business_id: resource.business_id,
              bookable_resource_id: resource.id,
              ...input,
            });
            toast.push({ title: "Blackout created", tone: "success" });
            setBlackoutOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <CreateMaintenanceDialog
        open={maintenanceOpen}
        onClose={() => {
          setMaintenanceOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.createMaintenance({
              business_id: resource.business_id,
              bookable_resource_id: resource.id,
              ...input,
            });
            toast.push({ title: "Maintenance created", tone: "success" });
            setMaintenanceOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <CreatePolicyDialog
        open={policyOpen}
        onClose={() => {
          setPolicyOpen(false);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          setBusy(true);
          setActionError(null);
          try {
            await api.createPolicy({
              business_id: resource.business_id,
              bookable_resource_id: resource.id,
              ...input,
            });
            toast.push({ title: "Policy created", tone: "success" });
            setPolicyOpen(false);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <ReasonActionDialog
        open={scheduleAction !== null}
        title={`${formatLabel(scheduleAction?.action ?? "action")} ${scheduleAction?.kind ?? "window"}`}
        onClose={() => {
          setScheduleAction(null);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (reason) => {
          if (!scheduleAction) return;
          setBusy(true);
          setActionError(null);
          try {
            if (scheduleAction.kind === "availability") {
              await api.applyAvailabilityAction(
                scheduleAction.id,
                scheduleAction.action,
                { reason },
              );
            } else {
              await api.applyBlackoutAction(
                scheduleAction.id,
                scheduleAction.action,
                { reason },
              );
            }
            toast.push({
              title: "Schedule updated",
              description: formatLabel(scheduleAction.action),
              tone: "success",
            });
            setScheduleAction(null);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <MaintenanceActionDialog
        open={maintTarget !== null}
        onClose={() => {
          setMaintTarget(null);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          if (!maintTarget) return;
          setBusy(true);
          setActionError(null);
          try {
            await api.applyMaintenanceAction(maintTarget, input.action, {
              ...(input.reason ? { reason: input.reason } : {}),
              ...(input.ends_at !== undefined ? { ends_at: input.ends_at } : {}),
            });
            toast.push({
              title: "Maintenance updated",
              description: formatLabel(input.action),
              tone: "success",
            });
            setMaintTarget(null);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <CreateImpactDialog
        open={impactTarget !== null}
        onClose={() => {
          setImpactTarget(null);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          if (!impactTarget) return;
          setBusy(true);
          setActionError(null);
          try {
            await api.createImpact(impactTarget, input);
            toast.push({ title: "Impact recorded", tone: "success" });
            setImpactTarget(null);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />

      <PolicyActionDialog
        open={policyTarget !== null}
        onClose={() => {
          setPolicyTarget(null);
          setActionError(null);
        }}
        busy={busy}
        error={actionError}
        onSubmit={async (input) => {
          if (!policyTarget) return;
          setBusy(true);
          setActionError(null);
          try {
            const { action, ...body } = input;
            await api.applyPolicyAction(policyTarget, action, body);
            toast.push({
              title: "Policy updated",
              description: formatLabel(action),
              tone: "success",
            });
            setPolicyTarget(null);
            await load();
          } catch (err) {
            setActionError(apiErrorMessage(err));
          } finally {
            setBusy(false);
          }
        }}
      />
    </Stack>
  );
}

function SupplySection({
  title,
  emptyTitle,
  emptyDescription,
  onAdd,
  addLabel,
  children,
}: {
  title: string;
  emptyTitle: string;
  emptyDescription: string;
  onAdd: () => void;
  addLabel: string;
  children?: ReactNode;
}) {
  return (
    <Surface>
      <Stack gap={16}>
        <Flex justify="space-between" align="center" wrap gap={8}>
          <Text role="h2" as="h2">
            {title}
          </Text>
          <Button size="sm" variant="secondary" onClick={onAdd}>
            {addLabel}
          </Button>
        </Flex>
        {children ?? (
          <EmptyState
            title={emptyTitle}
            description={emptyDescription}
            reason="none"
          />
        )}
      </Stack>
    </Surface>
  );
}

function AvailabilityTable({
  rows,
  onAction,
}: {
  rows: AvailabilityDto[];
  onAction: (id: string, action: ScheduleWindowAction) => void;
}) {
  const actions: ScheduleWindowAction[] = ["activate", "modify", "cancel", "expire"];
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Type</Th>
          <Th>Status</Th>
          <Th>Starts</Th>
          <Th>Ends</Th>
          <Th>Actions</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row) => (
          <Tr key={row.id}>
            <Td>{formatLabel(row.availability_type)}</Td>
            <Td>
              <StatusChip tone={scheduleStatusTone(row.status)}>
                {formatLabel(row.status)}
              </StatusChip>
            </Td>
            <Td>{row.starts_at}</Td>
            <Td>{row.ends_at}</Td>
            <Td>
              <Flex gap={4} wrap>
                {actions.map((action) => (
                  <Button
                    key={action}
                    size="sm"
                    variant="ghost"
                    onClick={() => onAction(row.id, action)}
                  >
                    {formatLabel(action)}
                  </Button>
                ))}
              </Flex>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

function BlackoutTable({
  rows,
  onAction,
}: {
  rows: BlackoutDto[];
  onAction: (id: string, action: ScheduleWindowAction) => void;
}) {
  const actions: ScheduleWindowAction[] = ["activate", "modify", "cancel", "expire"];
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Reason</Th>
          <Th>Status</Th>
          <Th>Starts</Th>
          <Th>Ends</Th>
          <Th>Actions</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row) => (
          <Tr key={row.id}>
            <Td>{formatLabel(row.reason_type)}</Td>
            <Td>
              <StatusChip tone={scheduleStatusTone(row.status)}>
                {formatLabel(row.status)}
              </StatusChip>
            </Td>
            <Td>{row.starts_at}</Td>
            <Td>{row.ends_at}</Td>
            <Td>
              <Flex gap={4} wrap>
                {actions.map((action) => (
                  <Button
                    key={action}
                    size="sm"
                    variant="ghost"
                    onClick={() => onAction(row.id, action)}
                  >
                    {formatLabel(action)}
                  </Button>
                ))}
              </Flex>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

function MaintenanceTable({
  rows,
  onAction,
  onImpact,
}: {
  rows: MaintenanceDto[];
  onAction: (id: string) => void;
  onImpact: (id: string) => void;
}) {
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Status</Th>
          <Th>Starts</Th>
          <Th>Ends</Th>
          <Th>Reason</Th>
          <Th>Actions</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row) => (
          <Tr key={row.id}>
            <Td>
              <StatusChip tone={scheduleStatusTone(row.status)}>
                {formatLabel(row.status)}
              </StatusChip>
            </Td>
            <Td>{row.starts_at}</Td>
            <Td>{row.ends_at ?? "—"}</Td>
            <Td>{row.reason ?? "—"}</Td>
            <Td>
              <Flex gap={4} wrap>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onAction(row.id)}
                >
                  Actions
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => onImpact(row.id)}
                >
                  Record impact
                </Button>
              </Flex>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}

function PolicyTable({
  rows,
  onAction,
}: {
  rows: PolicyDto[];
  onAction: (id: string) => void;
}) {
  return (
    <Table>
      <Thead>
        <Tr>
          <Th>Type</Th>
          <Th>Status</Th>
          <Th>Effective from</Th>
          <Th>Effective to</Th>
          <Th>Actions</Th>
        </Tr>
      </Thead>
      <Tbody>
        {rows.map((row) => (
          <Tr key={row.id}>
            <Td>{formatLabel(row.policy_type)}</Td>
            <Td>
              <StatusChip tone={scheduleStatusTone(row.status)}>
                {formatLabel(row.status)}
              </StatusChip>
            </Td>
            <Td>{row.effective_from}</Td>
            <Td>{row.effective_to ?? "—"}</Td>
            <Td>
              <Button size="sm" variant="ghost" onClick={() => onAction(row.id)}>
                Actions
              </Button>
            </Td>
          </Tr>
        ))}
      </Tbody>
    </Table>
  );
}
